import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Checkbox, FormControl, FormLabel, MenuItem, Select } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide, { SlideProps } from "@mui/material/Slide";
import { Backspace, Close } from "@mui/icons-material";
import { withStyles } from "tss-react/mui";
import notificationsStyle from "../../../assets/jss/material-dashboard-pro-react/views/notificationsStyle";
import Autocomplete from "../../../components/Autocomplete/Autocomplete";
import Button from "../../../components/CustomButtons/Button";
import ColorPicker from "../../../components/CustomInput/ColorPicker";
import CustomInput from "../../../components/CustomInput/CustomInput";
import CustomSwitch from "../../../components/CustomInput/CustomSwitch";
import CustomSwitchStringVal from "../../../components/CustomInput/CustomSwitchStringVal";
import NumberInput from "../../../components/CustomInput/NumberInput";
import SaveButton from "../../../components/CustomInput/SaveButton";
import GridContainer from "../../../components/Grid/GridContainer";
import GridItem from "../../../components/Grid/GridItem";
import TimeTable from "../../../components/TimeTable/TimeTable";
import TagList from "../../../components/TagList/TagList";
import TranslationWrapper, { getTranslation } from "../../../components/TranslationWrapper/TranslationWrapper";
import { __ } from "../../../translations/utils";
import { convertArrayToSelectOptions } from "../../../templateHelpers";
import { displayOnScreen } from "../../../variables/app";
import { toast } from "react-toastify";
import productenStyle from "../../../assets/jss/material-dashboard-pro-react/views/productStyle";
import jamezzFormStyles from "../../JamezzForm/JamezzFormStyles";
import customSelectStyle from "../../../assets/jss/material-dashboard-pro-react/customSelectStyle";
import modalStyle from "../../../assets/jss/material-dashboard-pro-react/modalStyle";

const Transition = React.forwardRef<HTMLDivElement, SlideProps>(function Transition({ children, ...props }, ref) {
  return (
    <Slide direction={"down"} ref={ref} {...props}>
      {children}
    </Slide>
  );
});

const validInputNames = [
  "prijs",
  "removeimages",
  "imgid",
  "blocked",
  "is_actief",
  "timeTable",
  "bigDescr",
  "requireAge",
  "depositId",
  "upsellMenu",
  "cartAfterAdding",
  "options",
  "tags",
  "vatPercentage",
  "fontColor",
  "bgColor",
  "onOrderscreen",
  "sortkey",
  "waitTime",
] as const;

type ValidInputNameKey = (typeof validInputNames)[number];

function MassEdit(props: {
  classes: { [k in string]: string };
  productIds: number[];
  toggle: () => void;
  refreshTable: () => void;
  open: boolean;
}) {
  const { classes } = props;
  const form = useRef<HTMLFormElement | null>(null);
  const [formData, setFormData] = useState<{
    prijs?: number;
    removeimages?: boolean;
    imgid?: string;
    blocked?: boolean;
    is_actief?: boolean;
    timeTable?: any;
    bigDescr?: string;
    requireAge?: number;
    depositId?: number | string;
    upsellMenu?: any;
    cartAfterAdding?: any;
    options?: any;
    tags?: any;
    vatPercentage?: number;
    fontColor?: string;
    bgColor?: string;
    onOrderscreen?: string;
    sortkey?: number;
    waitTime?: number;
  }>({});
  const [activeItems, setActiveItems] = useState<{ [k in ValidInputNameKey]?: boolean }>({});
  const [optionData, setOptionData] = useState<{ Opties?: string[] }>({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [tagMethod, setTagMethod] = useState<"sync" | "add" | "remove">("sync");
  const [sameSalesarea, setSameSalesarea] = useState(false);
  const [vestigingId, setVestigingId] = useState([]);
  const [activeOptions, setActiveOptions] = useState<{ [k in string]: boolean }>({});
  const [selectedOptions, setSelectedOptions] = useState<{ [k in string]: boolean }>({});
  const [tagListItems, setTagListItems] = useState([]);
  const [changeList, setChangeList] = useState<{ [k in string]: { [k in string]: boolean | null } | null }>({});
  const [imageList, setImageList] = useState<{ [k in ValidInputNameKey]?: string | ArrayBuffer | null }>({});
  const [prodImageList, setProdImageList] = useState<{ [k in ValidInputNameKey]?: string | ArrayBuffer | null }>({});
  const [tagListValue, setTagListValue] = useState([]);
  const kitchenDisplay = convertArrayToSelectOptions(displayOnScreen);

  let groupCount = 0;
  const groupList: { [k in string]: string } = {};
  const optionsView: JSX.Element[] = [];

  const toggleActiveItem = (field: ValidInputNameKey, state: boolean) => {
    const prevState = { ...activeItems };
    prevState[field] = state;
    setActiveItems(prevState);
  };

  const handleTagList = (name) => (value) => {
    setTagListValue(value);
  };

  const needsSameSalesarea = ["depositId", "upsellMenu", "tags"];

  useEffect(() => {
    const data = [
      {
        field: "id",
        operator: "in",
        value: props.productIds,
      },
    ];

    axios.post("core/producten/search?fields=vestiging_id&distinct=true", data).then((response) => {
      const sameSalesarea = response.data.data.data.length === 1;
      setSameSalesarea(sameSalesarea);

      if (sameSalesarea) {
        setVestigingId(response.data.data.data[0].vestiging_id);
      }
    });
  }, []);

  const canSave = () => {
    return (
      validInputNames.filter((input) => {
        return activeItems[input] === undefined || !activeItems[input];
      }).length !== validInputNames.length
    );
  };

  const inputSelect = validInputNames
    .filter((input) => {
      return activeItems[input] === undefined || !activeItems[input];
    })
    .filter((input) => {
      return !needsSameSalesarea.includes(input) || sameSalesarea;
    })
    .map((input) => {
      return (
        <MenuItem
          classes={{
            root: classes.selectMenuItem,
            selected: classes.selectMenuItemSelected,
          }}
          key={input}
          value={input}
        >
          {getTranslation("producten.form.algemeen.label." + input, input)}
        </MenuItem>
      );
    });

  const save = (e) => {
    e.preventDefault();
    setSaving(true);

    if (activeItems.options) {
      document.getElementById("optionsData").value = JSON.stringify(changeList);
    }

    const formElements = form.current?.elements ?? [];
    const data: {
      ids: number[];
      values: { field: ValidInputNameKey; value: (typeof formData)[ValidInputNameKey]; tag_method?: string }[];
      images: { name: ValidInputNameKey; data: string | ArrayBuffer | null }[];
      prodimages: { name: ValidInputNameKey; data: string | ArrayBuffer | null }[];
    } = {
      ids: props.productIds,
      values: [],
      images: [],
      prodimages: [],
    };

    Array.from(formElements).forEach((e) => {
      if ("name" in e && "value" in e && validInputNames.includes(e.name as ValidInputNameKey)) {
        const value = {
          field: e.name as ValidInputNameKey,
          value: e.value,
        };
        if (e.name) {
          value.tag_method = tagMethod;
        }

        data.values.push(value);
      }
    });

    /**
     * Add Images
     */
    for (const img in imageList) {
      data.images.push({
        name: img as ValidInputNameKey,
        data: imageList[img as ValidInputNameKey] ?? null,
      });
    }

    /**
     * Add other Images
     */
    for (const img in prodImageList) {
      data.prodimages.push({
        name: img as ValidInputNameKey,
        data: prodImageList[img as ValidInputNameKey] ?? null,
      });
    }

    axios.post("core/producten/mass_edit", data).then((response) => {
      props.toggle();
      props.refreshTable();
      toast.success(response.data.data.message);
      setSaving(false);
    });
  };

  const renderOptions = () => {
    for (const [key, value] of Object.entries(optionData)) {
      const subItems = [];
      for (let cc = 0; cc < value.length; cc++) {
        groupList["subItem" + groupCount + "-" + cc] = key;
        subItems.push(
          <div key={key + cc} style={{ marginLeft: "20px" }}>
            <Checkbox
              checked={activeOptions["aSubItem" + groupCount + "-" + cc] || false}
              id={"aSubItem" + groupCount + "-" + cc}
              name={"aSubItem" + groupCount + "-" + cc}
              onChange={(event) => {
                const groupName = groupList[event.target.attributes.id.value.replace("aSubItem", "subItem")];
                const targetElement = event.target.id.replace("aSubItem", "subItem");
                const optieName = document.getElementById(targetElement)?.value;
                setSelectedOptions({
                  ...selectedOptions,
                  [targetElement]: event.target.checked,
                });
                setActiveOptions({
                  ...activeOptions,
                  [event.target.attributes.id.value]: event.target.checked,
                });
                if (!event.target.checked && event.target.value in (changeList[groupName] ?? {})) {
                  let groupKeyCount = 0;
                  setChangeList({
                    ...changeList,
                    [groupName]: { ...changeList[groupName], [optieName]: null },
                  });
                  console.log(changeList);
                  for (const cKey in changeList[groupName]) {
                    if (changeList[groupName]?.[cKey] !== null) groupKeyCount++;
                  }
                  if (groupKeyCount == 0) {
                    setChangeList({ ...changeList, [groupName]: null });
                  }
                } else if (event.target.checked) {
                  if (!(groupName in changeList)) {
                    setChangeList({ ...changeList, [groupName]: {} });
                  }
                  setChangeList({
                    ...changeList,
                    [groupName]: { ...changeList[groupName], [optieName]: event.target.checked },
                  });
                }
              }}
              value={value[cc]}
            />
            <CustomSwitchStringVal
              checked={selectedOptions["subItem" + groupCount + "-" + cc] || false}
              disabled={!(activeOptions["aSubItem" + groupCount + "-" + cc] || false)}
              id={"subItem" + groupCount + "-" + cc}
              name={"subItem" + groupCount + "-" + cc}
              onChange={(event) => {
                const groupName = groupList[event.target.attributes.id.value];
                setSelectedOptions({
                  ...selectedOptions,
                  [event.target.name]: event.target.checked,
                });
                if (!(groupName in changeList)) {
                  setChangeList({ ...changeList, [groupName]: {} });
                }
                setChangeList({
                  ...changeList,
                  [groupName]: {
                    ...changeList[groupName],
                    [event.target.value]: event.target.checked,
                  },
                });
              }}
              value={value[cc]}
            />
            {value[cc]}
            <input
              accept={"image/*"}
              id={"subItemFile" + groupCount + "-" + cc}
              name={"[" + key + "]" + value[cc]}
              onChange={(event) => {
                const reader = new FileReader();
                const targetName = event.target.name;
                reader.onloadend = () => {
                  setImageList({
                    ...imageList,
                    [targetName]: reader.result,
                  });
                };
                reader.readAsDataURL(event.target.files[0]);
              }}
              style={{ marginLeft: "20px" }}
              type={"file"}
            />
          </div>
        );
      }
      optionsView.push(
        <div key={"optionsGroupBlock" + groupCount}>
          <Checkbox
            checked={activeOptions["aGroupName" + groupCount] || false}
            id={"aGroupName" + groupCount}
            name={"groupName" + groupCount}
            onChange={(event) => {
              const groupName = event.target.value;
              setSelectedOptions({
                ...selectedOptions,
                [event.target.name]: event.target.checked,
              });
              if (event.target.checked) {
                setChangeList({
                  ...changeList,
                  [groupName]: { ...changeList[groupName], _setState: event.target.checked },
                });
              } else {
                setChangeList({
                  ...changeList,
                  [groupName]: { ...changeList[groupName], _setState: null },
                });
              }
              setActiveOptions({
                ...activeOptions,
                [event.target.id]: event.target.checked,
              });
            }}
            value={key}
          />
          <CustomSwitchStringVal
            checked={selectedOptions["groupName" + groupCount] || false}
            disabled={!(activeOptions["aGroupName" + groupCount] || false)}
            id={"groupName" + groupCount}
            name={"groupName" + groupCount}
            onChange={(event) => {
              const groupName = event.target.value;
              setSelectedOptions({
                ...selectedOptions,
                [event.target.name]: event.target.checked,
              });
              setChangeList({
                ...changeList,
                [groupName]: { ...changeList[groupName], _setState: event.target.checked },
              });
            }}
            value={key}
          />
          {key}
          {subItems}
        </div>
      );
      groupCount++;
    }
    return (
      <div key={"displayMassOptions"}>
        <input id={"optionsData"} name={"options"} type={"hidden"} />
        {optionsView}
      </div>
    );
  };

  const editOptions = () => {
    if (!loading) {
      setLoading(true);
      axios
        .post("core/producten/mass_get_options", {
          ids: props.productIds,
        })
        .then((response) => {
          setOptionData(response.data.data);
        });
    }
  };

  const getTags = () => {
    if (!loadingTags) {
      setLoadingTags(true);
      axios
        .get(
          "core/tags?per_page=5000&fields=id,tagName as label&sort_field=tagName&sort_dir=asc&vestiging_id=" +
            vestigingId
        )
        .then((response) => {
          setTagListItems(response.data.data.data);
        });
    }
  };

  return (
    <Dialog
      TransitionComponent={Transition}
      aria-describedby={"notice-modal-slide-description"}
      aria-labelledby={"notice-modal-slide-title"}
      fullWidth={true}
      keepMounted
      maxWidth={"lg"}
      onClose={props.toggle}
      open={props.open}
    >
      <form onSubmit={save} ref={form}>
        <DialogTitle className={classes.modalHeader} disableTypography id={"notice-modal-slide-title"}>
          <Button
            aria-label={"Close"}
            className={classes.modalCloseButton}
            color={"transparent"}
            justIcon
            key={"close"}
            onClick={props.toggle}
          >
            <Close className={classes.modalClose} />
          </Button>
          <h4 className={classes.modalTitle}>{getTranslation("Producten aanpassen", "Producten aanpassen")}</h4>
        </DialogTitle>
        <DialogContent className={classes.modalBody} id={"notice-modal-slide-description"}>
          <GridContainer>
            <GridItem className={classes.selectInputGridItem} sm={12} xs={12}>
              <FormControl className={classes.selectFormControl} fullWidth>
                <Select
                  MenuProps={{
                    className: classes.selectMenu,
                  }}
                  classes={{
                    select: classes.select,
                  }}
                  displayEmpty
                  onChange={(e) => {
                    toggleActiveItem(e.target.value, true);
                  }}
                  value={""}
                >
                  <MenuItem
                    classes={{
                      root: classes.selectMenuItem,
                      selected: classes.selectMenuItemSelected,
                    }}
                    disabled
                    key={"empty"}
                    value={""}
                  >
                    {getTranslation("Kies wat je wilt aanpassen.", "Kies wat je wilt aanpassen")}
                  </MenuItem>
                  {inputSelect}
                </Select>
              </FormControl>
            </GridItem>
          </GridContainer>
          {activeItems.imgid ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>
                  {getTranslation("Product image", "Product image")}
                </FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <input
                  accept={"image/png,image/jpeg,image/jpg,image/gif"}
                  name={"imgid"}
                  onChange={() => {
                    const reader = new FileReader();
                    const targetName = event.srcElement.name;
                    reader.onloadend = () => {
                      setProdImageList({
                        ...prodImageList,
                        [targetName]: reader.result,
                      });
                    };
                    reader.readAsDataURL(event.target.files[0]);
                  }}
                  type={"file"}
                />
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("imgid", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.removeimages ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>
                  {getTranslation("Remove images", "Remove images")}
                </FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <input name={"removeimages"} type={"hidden"} value={true} />
                Remove images
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("removeimages", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.prijs ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>{getTranslation("Prijs", "Prijs")}</FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <NumberInput
                  currency={"EUR"}
                  id={"prijs"}
                  name={"prijs"}
                  onChange={(response) => {
                    setFormData({
                      ...formData,
                      prijs: response.value,
                    });
                  }}
                  type={"valuta"}
                  value={formData.prijs ? formData.prijs : 0}
                />
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("prijs", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.blocked ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>
                  {getTranslation("producten.form.algemeen.label.blocked", "Blocked")}
                </FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <CustomSwitch
                  checked={formData.blocked ? formData.blocked : false}
                  name={"blocked"}
                  onChange={() => {
                    setFormData({
                      ...formData,
                      blocked: event.target.checked,
                    });
                  }}
                />
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("blocked", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.is_actief ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>
                  {getTranslation("Actief (systeem)", "Actief (systeem)")}
                </FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <CustomSwitch
                  checked={formData.is_actief ? formData.is_actief : false}
                  name={"is_actief"}
                  onChange={() => {
                    setFormData({
                      ...formData,
                      is_actief: event.target.checked,
                    });
                  }}
                />
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("is_actief", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.timeTable ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>
                  {getTranslation("Tijd schema", "Tijd schema")}
                </FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <TimeTable data={formData.timeTable ? formData.timeTable : null} inputName={"timeTable"} />
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("timeTable", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.bigDescr ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>
                  {getTranslation("Omschrijving", "Omschrijving")}
                </FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <CustomInput
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    name: "bigDescr",
                    multiline: true,
                    rows: 10,
                  }}
                  value={""}
                />
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("bigDescr", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.sortkey ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>{getTranslation("Sortkey", "Sortkey")}</FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <CustomInput
                  formControlProps={{
                    fullWidth: true,
                  }}
                  id={"sortkey"}
                  inputProps={{
                    type: "number",
                    name: "sortkey",
                  }}
                />
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("sortkey", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.waitTime ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>{getTranslation("Waittime", "Waittime")}</FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <CustomInput
                  formControlProps={{
                    fullWidth: true,
                  }}
                  id={"waitTime"}
                  inputProps={{
                    type: "number",
                    name: "waitTime",
                  }}
                />
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("waitTime", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.requireAge ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>
                  {getTranslation("Leeftijdscheck", "Leeftijdscheck")}
                </FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <NumberInput
                  id={"requireAge"}
                  name={"requireAge"}
                  onChange={(response) => {
                    setFormData({
                      ...formData,
                      requireAge: response.value,
                    });
                  }}
                  suffix={" " + __("producten", "form.years.suffix", "Jaar")}
                  value={formData.requireAge ? formData.requireAge : 0}
                />
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("requireAge", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.depositId ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>
                  {getTranslation("Deposit product", "Deposit product")}
                </FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <Autocomplete
                  async={{
                    url:
                      "core/producten/search?fields=id as value, naam as label&per_page=10&vestiging_id=" + vestigingId,
                    searchField: "naam",
                  }}
                  hiddenFieldName={"depositId"}
                  onClick={(item) => {
                    setFormData({
                      ...formData,
                      depositId: item.value,
                    });
                  }}
                  placeHolderText={getTranslation("Filter on product", "Filter on product")}
                  value={formData.depositId ? formData.depositId : undefined}
                />
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("depositId", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.upsellMenu ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>
                  {getTranslation("Upsell menucard", "Upsell menucard")}
                </FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <Autocomplete
                  async={{
                    url:
                      "core/kaarten/search?fields=id as value, naam as label&per_page=10&vestiging_id=" + vestigingId,
                    searchField: "naam",
                  }}
                  hiddenFieldName={"upsellMenu"}
                  onClick={(item) => {
                    setFormData({
                      ...formData,
                      upsellMenu: item.value,
                    });
                  }}
                  placeHolderText={getTranslation("Filter on menucard", "Filter on menucard")}
                  value={formData.upsellMenu ? formData.upsellMenu : undefined}
                />
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("upsellMenu", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.cartAfterAdding ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>
                  {getTranslation(
                    "Go to shopping basket after adding this product",
                    "Go to shopping basket after adding this product"
                  )}
                </FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <CustomSwitch
                  checked={formData.cartAfterAdding ? formData.cartAfterAdding : false}
                  name={"cartAfterAdding"}
                  onChange={() => {
                    setFormData({
                      ...formData,
                      cartAfterAdding: event.target.checked,
                    });
                  }}
                />
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("cartAfterAdding", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.vatPercentage ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>{getTranslation("BTW", "BTW")}</FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <NumberInput
                  id={"vatPercentage"}
                  name={"vatPercentage"}
                  onChange={(response) => {
                    setFormData({
                      ...formData,
                      vatPercentage: response.value,
                    });
                  }}
                  suffix={" %"}
                  value={formData.vatPercentage ? formData.vatPercentage : 0}
                />
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("vatPercentage", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.options ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>{getTranslation("Opties", "Opties")}</FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                {editOptions()}
                {renderOptions()}
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("options", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
        </DialogContent>
        <DialogContent>
          {activeItems.tags ? (
            <GridContainer>
              {getTags()}
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>{getTranslation("Tags", "Tags")}</FormLabel>
              </GridItem>
              <GridItem sm={2} xs={12}>
                <TranslationWrapper tDefault={"Choose updating method"} tKey={"Choose updating method"} />
                <Select name={"tag_method"} onChange={(e) => setTagMethod(e.target.value)} value={tagMethod}>
                  <MenuItem value={"sync"}>
                    <TranslationWrapper tDefault={"Sync tags"} tKey={"Sync tags"} />
                  </MenuItem>
                  <MenuItem value={"add"}>
                    <TranslationWrapper tDefault={"Add tags"} tKey={"Add tags"} />
                  </MenuItem>
                  <MenuItem value={"remove"}>
                    <TranslationWrapper tDefault={"Remove tags"} tKey={"Remove tags"} />
                  </MenuItem>
                </Select>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <input name={"tags"} type={"hidden"} value={JSON.stringify(tagListValue)} />
                <TagList
                  dropdownText={getTranslation("Tags", "Tags")}
                  items={tagListItems}
                  onChange={handleTagList("id")}
                  selectedItems={tagListValue}
                  selectedText={getTranslation("Selected tag(s)", "Selected tag(s)")}
                  showHeaders={false}
                  singleSelect
                />
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("tags", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.fontColor ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>{getTranslation("Fontcolor", "Fontcolor")}</FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <ColorPicker
                  id={"fontColor"}
                  name={"fontColor"}
                  onChange={(colorname: ValidInputNameKey, color: { hex: string }) => {
                    setFormData({
                      ...formData,
                      fontColor: color.hex,
                    });
                  }}
                  value={formData.fontColor ? formData.fontColor : ""}
                />
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("fontColor", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.bgColor ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>
                  {getTranslation("Background color", "Background color")}
                </FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <ColorPicker
                  id={"bgColor"}
                  name={"bgColor"}
                  onChange={(colorname: ValidInputNameKey, color: { hex: string }) => {
                    setFormData({
                      ...formData,
                      bgColor: color.hex,
                    });
                  }}
                  value={formData.bgColor ? formData.bgColor : ""}
                />
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("bgColor", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
          {activeItems.onOrderscreen ? (
            <GridContainer>
              <GridItem sm={2} xs={12}>
                <FormLabel className={classes.labelHorizontal}>
                  {getTranslation("Display on kitchen screen", "Display on kitchen screen")}
                </FormLabel>
              </GridItem>
              <GridItem sm={9} xs={12}>
                <FormControl className={classes.selectFormControl} fullWidth>
                  <Select
                    MenuProps={{
                      className: classes.selectMenu,
                    }}
                    classes={{
                      select: classes.select,
                    }}
                    displayEmpty
                    name={"onOrderscreen"}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        onOrderscreen: e.target.value as string,
                      });
                    }}
                  >
                    {kitchenDisplay}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem sm={1} xs={12}>
                <Button
                  className={classes.actionButton}
                  color={"danger"}
                  onClick={() => toggleActiveItem("onOrderscreen", false)}
                  simple
                  style={{
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Backspace className={classes.icon} />
                </Button>
              </GridItem>
            </GridContainer>
          ) : null}
        </DialogContent>
        <DialogActions className={classes.modalFooter + " " + classes.modalFooterCenter}>
          <SaveButton
            buttonText={props.productIds.length}
            disabled={!canSave()}
            saving={saving}
            tDefault={"item(s) Aanpassen"}
            tKey={"modal.copy.button.mass_edit"}
            type={"submit"}
          />
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default withStyles(MassEdit, (theme) => ({
  ...productenStyle(theme),
  ...jamezzFormStyles(theme),
  ...customSelectStyle(theme),
  ...notificationsStyle(theme),
  ...modalStyle(theme),
}));
